import {
  CartSkuApiDto,
  ErrorResponseApiDto,
  OfferApiDto,
  OfferCampaignApiDto,
  ProductApiDto,
  SkuApiDto,
} from '@b2x/storefront-api-js-client/src/dto';
import { AxiosError } from 'axios';
import React from 'react';

import { useCartApi } from './api/useCartApi';
import { appConfig } from './config';

// Una riga di carrello può non esistere più lato server (id rigenerato/consumato da
// una mutazione precedente). In riconciliazione questo non è un errore bloccante:
// l'obiettivo "lo sku non deve essere nel carrello" è comunque raggiunto.
const isCartSkuNotFoundError = (error: unknown): boolean =>
  (error as AxiosError<ErrorResponseApiDto> | undefined)?.response?.data.key === 'api.error.CartSkuNotFoundException';

export interface UseCartCampaignSingleOfferBuyXGetYFlattenedSkusProps {
  campaign: OfferCampaignApiDto;
  campaignsCartSkus: Partial<Record<string, Array<CartSkuApiDto>>> | undefined;
}

export interface UseCartCampaignSingleOfferBuyXGetYFlattenedSkusResult {
  addToCartButtonEnabled: boolean;
  campaignCartSkus: Array<CartSkuApiDto> | undefined;
  handleAddToCartButtonClick(): Promise<void>;
  handlePickerSkuButtonClick(skuId: string): void;
  isLoadingSelectedSkuIds: boolean;
  offer: OfferApiDto;
  pickerSkus: Array<PickerSku> | undefined;
  selectedSkusIds: Array<string>;
}

export interface PickerSku extends Omit<SkuApiDto, 'product'> {
  buttonEnabled: boolean;
  product: ProductApiDto;
  selected: boolean;
}

export const useCartCampaignSingleOfferBuyXGetYFlattenedSkus = ({
  campaign,
  campaignsCartSkus,
}: UseCartCampaignSingleOfferBuyXGetYFlattenedSkusProps): UseCartCampaignSingleOfferBuyXGetYFlattenedSkusResult => {
  const [selectedSkusIds, setSelectedskusIds] = React.useState<Array<string>>([]);
  const [isLoadingSelectedSkuIds, setIsLoadingSelectedSkuIds] = React.useState<boolean>(true);

  const campaignName = React.useMemo<string>(() => {
    if (campaign.name === undefined) {
      throw new Error('CartCampaignWithoutName');
    }
    return campaign.name;
  }, [campaign.name]);

  const campaignCartSkus = React.useMemo(
    () => (campaignsCartSkus ? campaignsCartSkus[campaignName] : undefined),
    [campaignName, campaignsCartSkus]
  );

  React.useEffect(() => {
    const _selectedSkusIds: Array<string> = [];
    campaignCartSkus?.forEach((campaignCartSku) => {
      if (campaignCartSku.sku?.id !== undefined) {
        _selectedSkusIds.push(campaignCartSku.sku.id);
      }
    });
    setSelectedskusIds(_selectedSkusIds);
    setIsLoadingSelectedSkuIds(false);
  }, [campaignCartSkus]);

  const offer = React.useMemo<OfferApiDto>(() => {
    const _offer = campaign.offers?.at(0);
    if (_offer === undefined) {
      throw new Error('UseCartCampaignSingleOffer, but without any offer');
    }
    return _offer;
  }, [campaign.offers]);

  const [products, setProducts] = React.useState<Array<ProductApiDto>>();

  const { getOfferYProducts } = useCartApi();

  React.useEffect(() => {
    getOfferYProducts(offer.id, {
      populate: { pages: !!appConfig.cartCampaigns?.productsByCategoryTabs, skus: { price: true } },
    }).then((response) => {
      setProducts(response.data);
    });
  }, [getOfferYProducts, offer.id]);

  const pickerSkus = React.useMemo<Array<PickerSku> | undefined>(() => {
    let skus: Array<PickerSku> | undefined = undefined;
    if (products) {
      skus = [];
      products.forEach((product) => {
        product.skus?.forEach((sku) => {
          if (sku.state === 'AVAILABLE') {
            skus?.push({
              ...sku,
              buttonEnabled:
                offer.yQuantity === undefined ||
                selectedSkusIds.includes(sku.id) ||
                selectedSkusIds.length < offer.yQuantity,
              product: { ...product, skus: undefined },
              selected: selectedSkusIds.includes(sku.id),
            });
          }
        });
      });
    }
    return skus;
  }, [offer.yQuantity, products, selectedSkusIds]);

  const selectedSkus = React.useMemo(
    () => pickerSkus?.filter((pickerSku) => selectedSkusIds.includes(pickerSku.id)),
    [pickerSkus, selectedSkusIds]
  );

  const handlePickerSkuButtonClick = React.useCallback((skuId: string) => {
    setSelectedskusIds((prevState) =>
      prevState.includes(skuId) ? prevState.filter((_skuId) => _skuId !== skuId) : prevState.concat(skuId)
    );
  }, []);

  // Il flag "modifiche pendenti" abilita il bottone solo se la selezione corrente
  // differisce da ciò che è realmente nel carrello (evita add additivi inutili).
  const hasPendingChanges = React.useMemo(() => {
    const cartSkuIds = new Set<string>();
    campaignCartSkus?.forEach((campaignCartSku) => {
      if (campaignCartSku.sku?.id !== undefined) {
        cartSkuIds.add(campaignCartSku.sku.id);
      }
    });
    const uniqueSelectedIds = Array.from(new Set(selectedSkusIds));
    if (cartSkuIds.size !== uniqueSelectedIds.length) {
      return true;
    }
    if (uniqueSelectedIds.some((id) => !cartSkuIds.has(id))) {
      return true;
    }
    // Una riga di carrello con quantità diversa da 1 va comunque riconciliata.
    return !!campaignCartSkus?.some((campaignCartSku) => campaignCartSku.quantity !== 1);
  }, [campaignCartSkus, selectedSkusIds]);

  const addToCartButtonEnabled = React.useMemo(
    () => selectedSkusIds.length > 0 && hasPendingChanges,
    [selectedSkusIds.length, hasPendingChanges]
  );

  const { addSkus, changeSkuQuantity, getCart, removeSku, updateSessionCart } = useCartApi();

  // Gli id dei cartSku NON sono stabili: il backend li rigenera a ogni mutazione del
  // carrello. L'unico identificatore stabile è sku.id. Quindi, invece di fidarci degli
  // id catturati nello snapshot (campaignCartSkus, già potenzialmente obsoleto), prima
  // di ogni operazione rileggiamo il carrello dal server e risolviamo il cartSku.id
  // corrente a partire dallo sku.id.
  //
  // NB: usiamo getCart con un populate ESPLICITO di offers+sku, perché il populate della
  // sessione (sessionOptions.populate.cart) non garantisce la presenza di offers sui
  // cartSku; senza offers non potremmo riconoscere quali righe appartengono alla campagna.
  const fetchCurrentCampaignCartSkus = React.useCallback(async (): Promise<Array<CartSkuApiDto>> => {
    const response = await getCart({
      populate: {
        packs: {
          cartSkus: {
            offers: true,
            sku: true,
          },
        },
      },
    });
    const result: Array<CartSkuApiDto> = [];
    response.data.packs?.forEach((pack) => {
      pack.cartSkus?.forEach((cartSku) => {
        const belongsToThisCampaign = cartSku.offers?.some((o) => o.campaign && o.campaign === campaignName);
        if (belongsToThisCampaign) {
          result.push(cartSku);
        }
      });
    });
    return result;
  }, [getCart, campaignName]);

  const handleAddToCartButtonClick = React.useCallback(async (): Promise<void> => {
    if (!selectedSkus) {
      throw new Error('CartCampaignBuyXGetY - pickerSkus not loaded yet');
    }

    // Snapshot del target desiderato (cosa vogliamo che resti nel carrello dopo la riconciliazione).
    const desiredSkuIds = new Set(selectedSkus.map((s) => s.id));
    const selectedSkuById = new Map(selectedSkus.map((s) => [s.id, s]));

    // Stato server FRESCO: gli id qui sono quelli realmente validi per le mutazioni.
    let currentCartSkus = await fetchCurrentCampaignCartSkus();

    const currentSkuIds = new Set<string>();
    currentCartSkus.forEach((cartSku) => {
      if (cartSku.sku?.id !== undefined) {
        currentSkuIds.add(cartSku.sku.id);
      }
    });

    // Determino le operazioni in termini di sku.id (stabile), non di cartSku.id.
    const skuIdsToRemove = Array.from(currentSkuIds).filter((skuId) => !desiredSkuIds.has(skuId));
    const skuIdsToFixQuantity = currentCartSkus
      .filter((cartSku) => cartSku.sku?.id !== undefined && desiredSkuIds.has(cartSku.sku.id) && cartSku.quantity !== 1)
      .map((cartSku) => cartSku.sku?.id)
      .filter((skuId): skuId is string => skuId !== undefined);
    const skuIdsToAdd = Array.from(desiredSkuIds).filter((skuId) => !currentSkuIds.has(skuId));

    if (skuIdsToRemove.length === 0 && skuIdsToFixQuantity.length === 0 && skuIdsToAdd.length === 0) {
      return;
    }

    // Helper: trova il cartSku CORRENTE per uno sku.id nello stato fresco.
    const findCurrentCartSku = (skuId: string): CartSkuApiDto | undefined =>
      currentCartSkus.find((cartSku) => cartSku.sku?.id === skuId);

    // IMPORTANTE: le operazioni sono SEQUENZIALI e dopo OGNI mutazione rileggiamo il
    // carrello, perché ogni mutazione rigenera gli id dei cartSku lato backend.
    // Ordine: rimozioni -> fix quantità -> aggiunte.
    try {
      for (const skuId of skuIdsToRemove) {
        const cartSku = findCurrentCartSku(skuId);
        if (!cartSku) {
          // eslint-disable-next-line no-continue
          continue;
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          await removeSku({ cartSku });
        } catch (error) {
          if (!isCartSkuNotFoundError(error)) {
            throw error;
          }
        }
        // Rileggo lo stato fresco: gli id sono cambiati.
        // eslint-disable-next-line no-await-in-loop
        currentCartSkus = await fetchCurrentCampaignCartSkus();
      }

      for (const skuId of skuIdsToFixQuantity) {
        const cartSku = findCurrentCartSku(skuId);
        if (!cartSku) {
          // eslint-disable-next-line no-continue
          continue;
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          await changeSkuQuantity({ id: cartSku.id, quantity: 1 });
        } catch (error) {
          if (!isCartSkuNotFoundError(error)) {
            throw error;
          }
        }
        // eslint-disable-next-line no-await-in-loop
        currentCartSkus = await fetchCurrentCampaignCartSkus();
      }

      if (skuIdsToAdd.length > 0) {
        const skusToAdd = skuIdsToAdd
          .map((skuId) => selectedSkuById.get(skuId))
          .filter((sku): sku is PickerSku => sku !== undefined);
        await addSkus(
          skusToAdd.map((selectedSku) => ({
            offerId: offer.id,
            product: selectedSku.product,
            quantity: 1,
            sku: selectedSku,
          }))
        );
        // Per ora non chiamo analytics, manderei i prezzi del campionario, quando invece i prezzi sono soggetti allo sconto su Y.
        // analytics.events.addToCart(...)
      }

      // Refresh finale della sessione: removeSku/changeSkuQuantity aggiornano la sessione
      // in modo fire-and-forget, quindi forzo un aggiornamento atteso per garantire
      // che lo stato locale (campaignCartSkus) sia coerente al termine.
      await updateSessionCart({ silent: false });
    } catch (error) {
      throw error instanceof Error ? error : new Error(String(error));
    }
  }, [addSkus, changeSkuQuantity, removeSku, updateSessionCart, fetchCurrentCampaignCartSkus, offer.id, selectedSkus]);

  return {
    addToCartButtonEnabled,
    campaignCartSkus,
    handleAddToCartButtonClick,
    handlePickerSkuButtonClick,
    isLoadingSelectedSkuIds,
    offer,
    pickerSkus,
    selectedSkusIds,
  };
};
